import { ComponentProps, ReactNode } from "react";

import { CSSObject } from "@emotion/react";
import {
  Button,
  TooltipDialog,
  LoadingSpinner,
  type SvgComponentType,
} from "@newsela/angelou";
import { withTrackEvent } from "@newsela/snitch";

import { DoubleBarData, TripleBarData } from "@/components/DataInfoCard/types";
import ResponseError from "@/errors/ResponseError";
import InfoIcon from "@/icons/InfoIcon";
import AdminReportError from "@/reports/admin/AdminReportError";
import {
  defaultCardContentCss,
  cardContentWithHeadingCss,
  containerCss,
  headingContainerCss,
  headingTextCss,
  horizontalCardContentCss,
  tooltipIconCss,
  tooltipDialogContainerCss,
  toolTipCss,
  defaultLoadingCss,
  getDataCardColor,
} from "./DataInfoCard.style";
import DataValueOrIcon from "./DataValueOrIcon";
import DoubleBars from "./DoubleBars/DoubleBars";
import TripleHorizontalBars from "./TripleHorizontalBars";

const TooltipDialogWithTrackEvent = withTrackEvent(TooltipDialog);

export enum DataInfoCardVariant {
  circle = "circle",
  default = "default",
  progressCircle = "progressCircle",
  icon = "icon",
  doubleBar = "doubleBar",
  tripleHorizontalBars = "tripleHorizontalBar",
}

export enum DataInfoCardOrientation {
  vertical,
  horizontal,
}

export interface StatValueProps {
  /**
   *  The value of the stat number displayed prominently on the card.
   */
  statValue?: number | undefined;
  /**
   *  Any additional number or string displayed to the right of the statValue.
   */
  statValueAddOn?: number | string | undefined;

  /**
   *  Any additional number or string displayed to the left of the statValue.
   */
  statValuePrefix?: number | string | undefined;

  /**
   * Visual style variant for the stat value.
   */
  variant?: DataInfoCardVariant;

  /**
   * When the progressCircle variant is set, you may set a
   * base value to calculate the circle's open position.
   *
   * Defaults to 100.
   *
   * e.g. 60 for average minutes
   */
  progressPercentBase?: number | undefined;

  /**
   * Icon SVG component to use if icon variant is set.
   */
  iconSvgComponent?: SvgComponentType | ReactNode;

  /**
   * Text to be displayed to the right of the icon or stat value
   */
  statusText?: string | undefined;

  /**
   * Display a percentage symbol next to the stat value.
   */
  showPercentageSymbol?: boolean;
  /**
   * Any additional styles that can be added to the component.
   */
  additionalStyles?:
    | {
        /**
         * Any additional styles that can be added to the container.
         */
        containerCss?: CSSObject | undefined;
        /**
         * Any additional styles that can be added to the statValue container.
         * This passed into the DataValueOrIcon component.
         */
        statValueCss?: CSSObject | undefined;
        /**
         * Any additional styles that can be added to the statValueAddOn prop.
         * This passed into the DataValueOrIcon component.
         */
        statValueAddOnCss?: CSSObject | undefined;
        /**
         * Any additional styles that can be added to the statValueNumber displayed.
         * This passed into the DataValueOrIcon component.
         */
        statValueNumberCss?: CSSObject | undefined;
        /**
         * Any additional styles that can be added to the backgroundColor of the loading shimmer.
         */
        loadingBarOneBgColorCss?: string | undefined;
        /**
         * Any additional styles that can be added to the shimmerColor of the loading shimmer.
         */
        loadingBarOneShimmerColorCss?: string | undefined;
        /**
         * Any additional styles that can be added to the shimmerColor of the loading shimmer.
         */
        loadingBarTwoBgColorCss?: string | undefined;
        /**
         * Any additional styles that can be added to the shimmerColor of the loading shimmer.
         */
        loadingBarThreeBgColorCss?: string | undefined;
        /**
         * Differentiate colors for data info card depending on if the data info card is coming from teacher vs student overview.
         */
        studentCardColor?: string | undefined;
        /**
         * Any additional styles that can be added to the heading text.
         */
        headingTextCss?: CSSObject | undefined;
        /**
         * Any additional styles that can be added to the card content.
         */
        cardContentCss?: CSSObject | undefined;
        /**
         * Any additional styles that can be added to the heading container.
         */
        headingContainerCss?: CSSObject | undefined;
      }
    | undefined;
  /**
   *  The loading state of the card's icon if the DataInfoCard variant is set to 'icon'.
   */
  loading?: boolean | undefined;
}

interface DataInfoCardProps extends StatValueProps {
  /**
   * Text for the card heading.
   */
  heading?: string;

  /**
   * The HTML element type for the heading.
   *
   * Default: h3
   */
  headingElementType?: "h2" | "h3";

  /**
   * Card's textual content as a string or React component.
   */
  textContent?: ReactNode;
  /**
   * Tooltip props, inherits props from Angelou's TooltipDialog
   */
  tooltipDialog?: ComponentProps<typeof TooltipDialog>;

  /**
   * Prop to determine if the card content should be stacked vertically or horizontally.
   *
   * Default: vertical
   */
  orientation?: DataInfoCardOrientation;
  /**
   * Prop to relay the numeric data-point and accompanying label of each bar in the double-bar graph.
   */
  doubleBarData?: {
    barOne: DoubleBarData;
    barTwo: DoubleBarData;
  };
  /**
   * Prop to relay the numeric data-point and accompanying label of each bar
   * in the triple horizontal bar graph.
   */
  tripleBarData?: {
    barOne: TripleBarData;
    barTwo: TripleBarData;
    barThree?: TripleBarData | undefined;
    doubleBars?: boolean | undefined;
  };
  error?: boolean | undefined | typeof ResponseError;
  /**
   *  The tooltip description that will be passed to the event tracking object.
   */
  tooltipDialogEventTrackingDescription?: string | undefined;
}

const DataInfoCard = ({
  heading,
  headingElementType = "h3",
  progressPercentBase,
  statValue,
  statValueAddOn,
  statValuePrefix,
  additionalStyles,
  variant = DataInfoCardVariant.default,
  orientation = DataInfoCardOrientation.vertical,
  showPercentageSymbol = false,
  textContent,
  tooltipDialog,
  tooltipDialogEventTrackingDescription,
  iconSvgComponent,
  statusText,
  doubleBarData,
  tripleBarData,
  loading,
  error,
}: DataInfoCardProps) => {
  const variantsWithCircleWidget: DataInfoCardVariant[] = [
    DataInfoCardVariant.icon,
    DataInfoCardVariant.circle,
    DataInfoCardVariant.progressCircle,
  ];
  const renderCardContent = () => {
    const color = getDataCardColor(additionalStyles?.studentCardColor);
    if (variant === DataInfoCardVariant.doubleBar) {
      if (error) {
        return <AdminReportError svgColor={color} />;
      }
      return (
        <DoubleBars
          doubleBarData={doubleBarData}
          additionalStyles={additionalStyles}
          loading={loading}
        />
      );
    } else if (variant === DataInfoCardVariant.tripleHorizontalBars) {
      if (error) {
        return <AdminReportError svgColor={color} />;
      }
      return (
        <TripleHorizontalBars
          tripleBarData={tripleBarData}
          additionalStyles={additionalStyles}
          loading={loading}
        />
      );
    } else {
      if (loading && !variantsWithCircleWidget.includes(variant)) {
        return (
          <div css={defaultLoadingCss}>
            <LoadingSpinner />
          </div>
        );
      }
      if (error) {
        return <AdminReportError svgColor={color} />;
      }
      return (
        <div
          css={[
            defaultCardContentCss,
            !!heading && cardContentWithHeadingCss,
            orientation === DataInfoCardOrientation.horizontal &&
              horizontalCardContentCss,
            additionalStyles?.cardContentCss,
          ]}
        >
          <DataValueOrIcon
            {...{
              additionalStyles,
              statValue,
              statValueAddOn,
              statValuePrefix,
              variant,
              progressPercentBase,
              iconSvgComponent,
              statusText,
              showPercentageSymbol,
              loading,
            }}
          />
          {textContent && <div>{textContent}</div>}
        </div>
      );
    }
  };

  const defaultTooltipDialogAriaProps = {
    "aria-label": heading ? `More info about ${heading}` : "More info",
  };

  const renderHeadingText = () => {
    const HeadingElement = headingElementType;
    return (
      <HeadingElement css={[headingTextCss, additionalStyles?.headingTextCss]}>
        {heading}
      </HeadingElement>
    );
  };

  return (
    <div css={[containerCss, additionalStyles?.containerCss]}>
      {heading && (
        <div css={[headingContainerCss, additionalStyles?.headingContainerCss]}>
          {renderHeadingText()}
        </div>
      )}
      {tooltipDialog && (
        <div css={tooltipDialogContainerCss}>
          <TooltipDialogWithTrackEvent
            // @ts-expect-error Property '__cssFor' does not exist on type
            __cssFor={{ dialog: toolTipCss }}
            // @ts-expect-error Property 'css' does not exist on type
            buttonContents={<InfoIcon css={tooltipIconCss} />}
            buttonProps={{
              flavor: Button.flavors.incognito,
            }}
            placement="top"
            dialogAriaProps={defaultTooltipDialogAriaProps}
            ariaProps={defaultTooltipDialogAriaProps}
            {...tooltipDialog}
            actionName="Interact"
            objectType="User"
            streamProperties={{
              description: tooltipDialogEventTrackingDescription ?? "",
            }}
          />
        </div>
      )}
      {renderCardContent()}
    </div>
  );
};

export default DataInfoCard;
